import { useCallback, useEffect, useRef, useState } from "react";

/**
 * usePendingCaptureInsert — place a capture into a Segment that is NOT the one
 * currently open on the canvas.
 *
 * WHY THIS EXISTS. The builder holds ONE craft.js canvas, showing ONE Segment
 * at a time. Kelly asked for a picker listing EVERY segment ("all of the
 * segments should drop down and you get to pick the segment you want to drop it
 * in"), so choosing a segment other than the open one means: switch the canvas
 * first, wait for it to actually BE that segment, and only then insert.
 *
 * Inserting immediately after requesting the switch is a race that puts the
 * block in the WRONG SEGMENT — a silent data error the user would only find
 * later, which is strictly worse than the bug being fixed. So the insert is
 * held as PENDING and performed once the active segment id has genuinely
 * changed to the target.
 *
 * The wait is BOUNDED. A switch that never completes resolves as a stated
 * failure, never as a spinner that hangs and never as a silent no-op.
 *
 * daniyal-0909-stg-capture-add-to-both-segments-drops-one: switching Segments
 * re-keys the craft `<Frame>` (`components/SegmentView/index.jsx`, `key=
 * {selectedArc?.id}`) so a stale/wiped Frame from the PREVIOUS Segment is
 * never reconciled in place (ISP-9999 V2). That remount is exactly what a
 * Segment switch needs — but craft.js's own `Frame` mount does its node-id
 * bookkeeping by calling a state-updating "onChange" handler DURING RENDER
 * (`@craftjs/utils/useEffectOnce`'s nanoid dedupe, called from
 * `Frame.tsx`), which React documents/warns about ("Cannot update a
 * component while rendering a different component") as a render-phase
 * update on another fiber. MEASURED via a real
 * `CaptureSegmentDropProvider` + a real, identically-keyed `<Frame>` +
 * `@craftjs/core` mount (`addToBothSegments.integration.test.jsx`): once a
 * Segment switch triggers that remount, THIS hook's own watching effect
 * stops re-running — `activeSegmentId` genuinely reaches the target
 * Segment in the Redux store, but no further render of this hook's owning
 * component (`CaptureSegmentDropProvider`) is ever observed, so an effect
 * that only fires "when React re-renders this component" never gets a
 * chance to notice the match. The placement hangs until
 * `SEGMENT_SWITCH_TIMEOUT_MS`. Confirmed live on web-stg.iyuga.net too:
 * adding one capture to two Segments in one confirm placed it only in
 * whichever Segment was already open (no switch, no Frame remount, no
 * interference) — the other Segment, which needed a real switch, silently
 * got nothing, 6/6 trials.
 *
 * Fix: track the pending target in a REF (`pendingRef`, always current the
 * instant `place()` sets it) and poll for the match on a short interval in
 * ADDITION to the normal per-render effect, instead of depending solely on
 * this component getting a further render after the switch is dispatched.
 * The poll reads the CURRENT selection via `getActiveSegmentId` — a callback
 * the caller wires straight to the Redux store (`store.getState()`), not the
 * `activeSegmentId` PROP — because the prop is exactly what can go stale when
 * this component's own re-render is the thing that gets starved. The poll is
 * cheap (a store read + ref comparison) and self-cancels the moment it either
 * matches or the placement is settled by any other path. `isPending` stays a
 * small piece of real state purely so consumers re-render when placement
 * starts/stops.
 */

/** How often to poll for the canvas having become the target Segment. */
const SWITCH_POLL_INTERVAL_MS = 50;

/** How long to wait for the canvas to become the target Segment. */
export const SEGMENT_SWITCH_TIMEOUT_MS = 8000;

const usePendingCaptureInsert = ({
  activeSegmentId,
  getActiveSegmentId,
  onSelectSegment,
  insert,
}) => {
  const pendingRef = useRef(null);
  const [isPending, setIsPending] = useState(false);
  const resolveRef = useRef(null);
  const timerRef = useRef(null);
  const pollRef = useRef(null);
  const insertRef = useRef(insert);
  insertRef.current = insert;
  const getActiveSegmentIdRef = useRef(getActiveSegmentId);
  getActiveSegmentIdRef.current = getActiveSegmentId;
  // Which pending placement has ALREADY been consumed. This is the
  // one-add-per-action guard (kelly-0824-capture-note-double-add): both the
  // effect and the poll below can observe the same match, so a placement
  // that resolves via `insert` must not be re-applied by whichever of the
  // two runs second. Claiming the pending object in a ref, synchronously
  // before the first insert (G-114), makes the placement idempotent
  // regardless of which path (or how many times) it fires. A later
  // placement is a NEW pending object, so it is never wrongly skipped.
  const consumedRef = useRef(null);

  const settle = useCallback((result) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    const resolve = resolveRef.current;
    resolveRef.current = null;
    pendingRef.current = null;
    setIsPending(false);
    resolve?.(result);
  }, []);

  /**
   * Perform the held insert the moment the canvas really is the target
   * Segment. Shared by the per-render effect below AND `place()`'s poll —
   * see the header comment for why a render-triggered effect alone is not
   * reliable here (a Segment switch remounts the craft `<Frame>`, and that
   * remount can starve this hook's owning component of the further render
   * the effect needs to ever re-run).
   */
  const tryConsume = useCallback((targetActiveSegmentId) => {
    const pending = pendingRef.current;
    if (!pending) return;
    if (targetActiveSegmentId !== pending.segmentId) return;
    if (consumedRef.current === pending) return;
    consumedRef.current = pending;
    const outcome = insertRef.current(pending.item);
    settle(
      outcome.status === "ok"
        ? { ok: true }
        : { ok: false, message: outcome.message }
    );
  }, [settle]);

  // No dependency array: re-check on every single render, not only on
  // renders where React considers a dependency to have changed relative to
  // the previous one.
  useEffect(() => {
    tryConsume(activeSegmentId);
  });

  // Clear any outstanding timer/poll if the component goes away mid-switch.
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (pollRef.current) clearInterval(pollRef.current);
    },
    []
  );

  /**
   * Place `item` into `segmentId`. Resolves to { ok, message } — never throws,
   * never resolves silently ambiguous.
   *
   * `skipSelect` — the caller has ALREADY caused the canvas to switch to
   * `segmentId` by some other action (e.g. just creating that Segment, which
   * lands selected) and only needs this hook to wait for that switch to land
   * before inserting. Calling `onSelectSegment` in that case would be
   * redundant at best; at worst it re-reads a not-yet-updated Segment list and
   * silently no-ops the switch (the new Segment is not in it yet).
   */
  const place = useCallback(
    (item, segmentId, { skipSelect = false } = {}) => {
      // Already looking at the target: insert now, no switch, no wait.
      if (segmentId === activeSegmentId) {
        const outcome = insert(item);
        return Promise.resolve(
          outcome.status === "ok"
            ? { ok: true }
            : { ok: false, message: outcome.message }
        );
      }

      if (!skipSelect && !onSelectSegment) {
        return Promise.resolve({
          ok: false,
          message: "Open that Segment first, then add the capture to it.",
        });
      }

      return new Promise((resolve) => {
        resolveRef.current = resolve;
        pendingRef.current = { item, segmentId };
        setIsPending(true);
        timerRef.current = setTimeout(() => {
          settle({
            ok: false,
            message: "That Segment didn't open in time. Please try again.",
          });
        }, SEGMENT_SWITCH_TIMEOUT_MS);
        // Belt-and-suspenders alongside the per-render effect above: poll the
        // Redux store DIRECTLY (bypassing this component's own `activeSegmentId`
        // prop, which is exactly what can go stale — see the header comment),
        // so the switch is still detected even on a run where this
        // component's own render gets starved by the Frame remount it
        // triggers.
        if (getActiveSegmentIdRef.current) {
          pollRef.current = setInterval(() => {
            tryConsume(getActiveSegmentIdRef.current());
          }, SWITCH_POLL_INTERVAL_MS);
        }
        if (!skipSelect) onSelectSegment(segmentId);
      });
    },
    [activeSegmentId, insert, onSelectSegment, settle, tryConsume]
  );

  return { place, isPending };
};

export default usePendingCaptureInsert;
